import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useRememberListUrl } from '../lib/listReturn';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

interface DRow {
  id: string; jobId: string; jobStatus: string;
  dispatchOrderDate: string; totalBoxDispatch: string; createdOn: string | null;
}
interface ListResp { rows: DRow[]; total: number }

export function DispatchesPage() {
  useRememberListUrl();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get('page') ?? '1'));
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);

  const list = useQuery({
    queryKey: ['dispatches', { page, q }],
    queryFn: () =>
      api<ListResp>(`/api/dispatches?page=${page}&pageSize=50` + (q ? `&q=${encodeURIComponent(q)}` : '')),
  });
  const rows = list.data?.rows ?? [];

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    const p = new URLSearchParams(params);
    if (search) p.set('q', search); else p.delete('q');
    p.set('page', '1');
    setParams(p);
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-brand">Dispatches</h1>
          <p className="text-xs text-slate-500">{list.data?.total ?? 0} records</p>
        </div>
        <Link to="/dispatches/new" className="btn-primary">+ New Dispatch</Link>
      </div>

      <form className="flex gap-2 items-end" onSubmit={applySearch}>
        <div className="flex-1">
          <label className="label">Search</label>
          <input className="input" placeholder="Dispatch ID, Job ID…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <button className="btn-primary" type="submit">Search</button>
      </form>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2">Dispatch ID</th>
                <th className="px-3 py-2">Job ID</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Order Date</th>
                <th className="px-3 py-2 text-right">Boxes Dispatched</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && <tr><td colSpan={6} className="p-6 text-center text-slate-500">Loading…</td></tr>}
              {!list.isLoading && rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-500">No records.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">{r.id}</td>
                  <td className="px-3 py-2">{r.jobId}</td>
                  <td className="px-3 py-2 text-xs">{r.jobStatus}</td>
                  <td className="px-3 py-2 text-xs">{r.dispatchOrderDate}</td>
                  <td className="px-3 py-2 text-right">{r.totalBoxDispatch}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-3">
                      <Link to={`/dispatches/${r.id}/edit`} className="text-brand hover:underline text-sm">Edit</Link>
                      {r.jobId && <Link to={`/job-orders/${encodeURIComponent(r.jobId)}/print`} className="text-red-600 hover:underline text-sm">Card</Link>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
